import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, Calendar as CalendarIcon, Plus, ChevronLeft, ChevronRight,
  MapPin, Clock, Users, Landmark, Download, Check, Heart, X,
  Edit, Trash2, MoreHorizontal
} from 'lucide-react';
import { InfoTooltip } from '../components/ExplanationLayers';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * LIKEKIND CALENDAR V1 (LOCKED BUILD)
 * 
 * Purpose: Personal coordination, Groups & Commons scheduling
 * NOT: productivity app, task manager, social feed
 * 
 * Principles:
 * - No gamification, no streaks, no nudges
 * - No engagement metrics
 * - Calendar ≠ feed, Calendar ≠ SCUTA
 */

// View modes
const VIEW_MODES = ['day', 'week', 'month'];

// Scope filters
const SCOPE_FILTERS = [
  { id: 'all', label: 'All', icon: CalendarIcon },
  { id: 'personal', label: 'Personal', icon: CalendarIcon },
  { id: 'group', label: 'Groups', icon: Users },
  { id: 'commons', label: 'Commons', icon: Landmark }
];

// RSVP options
const RSVP_OPTIONS = [
  { status: 'going', label: 'Going', icon: Check, color: 'var(--lk-primary-green)' },
  { status: 'interested', label: 'Interested', icon: Heart, color: 'var(--lk-secondary-orange)' },
  { status: 'cant_make', label: "Can't", icon: X, color: 'var(--fy-text-disabled)' }
];

export default function Calendar() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useContext(AuthContext);
  
  // State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('week');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  
  // Load events
  useEffect(() => {
    loadEvents();
  }, [scopeFilter, currentDate]);
  
  const loadEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Calculate date range based on view
      const startDate = getStartOfView(currentDate, viewMode);
      const endDate = getEndOfView(currentDate, viewMode);
      
      const params = new URLSearchParams({
        scope: scopeFilter,
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString()
      });
      
      const res = await axios.get(`${API}/api/calendar/events?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEvents(res.data.events || []);
    } catch (err) {
      console.error('Failed to load events:', err);
      toast.error('Failed to load calendar');
    } finally {
      setLoading(false);
    }
  };
  
  const getStartOfView = (date, view) => {
    const d = new Date(date);
    if (view === 'day') {
      d.setHours(0, 0, 0, 0);
    } else if (view === 'week') {
      const day = d.getDay();
      d.setDate(d.getDate() - day);
      d.setHours(0, 0, 0, 0);
    } else {
      d.setDate(1);
      d.setHours(0, 0, 0, 0);
    }
    return d;
  };
  
  const getEndOfView = (date, view) => {
    const d = new Date(date);
    if (view === 'day') {
      d.setHours(23, 59, 59, 999);
    } else if (view === 'week') {
      const day = d.getDay();
      d.setDate(d.getDate() + (6 - day));
      d.setHours(23, 59, 59, 999);
    } else {
      d.setMonth(d.getMonth() + 1);
      d.setDate(0);
      d.setHours(23, 59, 59, 999);
    }
    return d;
  };
  
  const navigateDate = (direction) => {
    const d = new Date(currentDate);
    if (viewMode === 'day') {
      d.setDate(d.getDate() + direction);
    } else if (viewMode === 'week') {
      d.setDate(d.getDate() + (direction * 7));
    } else {
      d.setMonth(d.getMonth() + direction);
    }
    setCurrentDate(d);
  };
  
  const handleRSVP = async (eventId, status) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/calendar/events/${eventId}/rsvp`, 
        { status },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Marked as ${status.replace('_', ' ')}`);
      loadEvents();
    } catch (err) {
      toast.error('Failed to update RSVP');
    }
  };
  
  const handleExport = async (rangeDays) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        scope: scopeFilter,
        range_days: rangeDays.toString()
      });
      
      const res = await axios.get(`${API}/api/calendar/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Download the ICS file
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `likekind_${scopeFilter}_calendar.ics`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success('Calendar exported');
      setShowExportModal(false);
    } catch (err) {
      toast.error('Failed to export calendar');
    }
  };
  
  const formatDateRange = () => {
    const options = { month: 'short', day: 'numeric', year: 'numeric' };
    if (viewMode === 'day') {
      return currentDate.toLocaleDateString('en-US', { weekday: 'long', ...options });
    } else if (viewMode === 'week') {
      const start = getStartOfView(currentDate, 'week');
      const end = getEndOfView(currentDate, 'week');
      return `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${end.toLocaleDateString('en-US', options)}`;
    } else {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
  };
  
  const getScopeColor = (scope) => {
    switch (scope) {
      case 'personal': return 'var(--lk-primary-green)';
      case 'group': return 'var(--lk-primary-green)';
      case 'commons': return 'var(--lk-earth-brown)';
      default: return 'var(--fy-text-primary)';
    }
  };
  
  const getScopeIcon = (scope) => {
    switch (scope) {
      case 'personal': return <CalendarIcon className="w-4 h-4" />;
      case 'group': return <Users className="w-4 h-4" />;
      case 'commons': return <Landmark className="w-4 h-4" />;
      default: return <CalendarIcon className="w-4 h-4" />;
    }
  };
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--fy-background-primary)' }}>
      {/* Header */}
      <header className="sticky top-0 z-50" style={{ backgroundColor: 'var(--fy-background-primary)', borderBottom: '1px solid var(--fy-border-subtle)' }}>
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/my-world')} variant="ghost" size="icon">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-1">
                <h1 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Calendar</h1>
                <InfoTooltip topic="calendar" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button onClick={() => setShowExportModal(true)} variant="outline" size="sm">
                <Download className="w-4 h-4 mr-1" />
                Export
              </Button>
              <Button onClick={() => setShowCreateModal(true)} size="sm" style={{ backgroundColor: 'var(--lk-primary-green)', color: 'var(--lk-warm-sand)' }}>
                <Plus className="w-4 h-4 mr-1" />
                Event
              </Button>
            </div>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-4">
        {/* Scope Filters */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {SCOPE_FILTERS.map(filter => (
            <button
              key={filter.id}
              onClick={() => setScopeFilter(filter.id)}
              className={`px-4 py-2 rounded-full text-sm font-medium flex items-center gap-2 whitespace-nowrap transition-colors ${
                scopeFilter === filter.id ? 'text-white' : ''
              }`}
              style={{
                backgroundColor: scopeFilter === filter.id ? 'var(--lk-primary-green)' : 'var(--fy-surface-muted)',
                color: scopeFilter === filter.id ? 'var(--lk-warm-sand)' : 'var(--fy-text-secondary)'
              }}
              data-testid={`filter-${filter.id}`}
            >
              <filter.icon className="w-4 h-4" />
              {filter.label}
            </button>
          ))}
        </div>
        
        {/* Date Navigation */}
        <Card className="p-4 mb-4" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
          <div className="flex items-center justify-between">
            <Button onClick={() => navigateDate(-1)} variant="ghost" size="icon">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            
            <div className="text-center">
              <p className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>
                {formatDateRange()}
              </p>
              <div className="flex gap-2 mt-2 justify-center">
                {VIEW_MODES.map(mode => (
                  <button
                    key={mode}
                    onClick={() => setViewMode(mode)}
                    className={`px-3 py-1 rounded text-xs font-medium capitalize ${
                      viewMode === mode ? 'text-white' : ''
                    }`}
                    style={{
                      backgroundColor: viewMode === mode ? 'var(--lk-primary-green)' : 'transparent',
                      color: viewMode === mode ? 'var(--lk-warm-sand)' : 'var(--fy-text-secondary)'
                    }}
                  >
                    {mode}
                  </button>
                ))}
              </div>
            </div>
            
            <Button onClick={() => navigateDate(1)} variant="ghost" size="icon">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
          
          {/* Today button */}
          <div className="text-center mt-2">
            <button
              onClick={() => setCurrentDate(new Date())}
              className="text-xs font-medium"
              style={{ color: 'var(--lk-primary-green)' }}
            >
              Today
            </button>
          </div>
        </Card>
        
        {/* Events List */}
        {loading ? (
          <div className="text-center py-12">
            <CalendarIcon className="w-8 h-8 mx-auto mb-2 animate-pulse" style={{ color: 'var(--fy-text-disabled)' }} />
            <p style={{ color: 'var(--fy-text-secondary)' }}>Loading events...</p>
          </div>
        ) : events.length === 0 ? (
          <Card className="p-8 text-center" style={{ backgroundColor: 'var(--fy-surface-muted)' }}>
            <CalendarIcon className="w-12 h-12 mx-auto mb-3" style={{ color: 'var(--fy-text-disabled)' }} />
            <p className="font-medium mb-2" style={{ color: 'var(--fy-text-primary)' }}>No events</p>
            <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Create a personal event or join a group to see events here.
            </p>
            <Button onClick={() => setShowCreateModal(true)} size="sm" style={{ backgroundColor: 'var(--lk-primary-green)', color: 'var(--lk-warm-sand)' }}>
              <Plus className="w-4 h-4 mr-1" />
              Create Event
            </Button>
          </Card>
        ) : (
          <div className="space-y-3">
            {events.map(event => (
              <EventCard 
                key={event.event_id} 
                event={event} 
                onRSVP={handleRSVP}
                onClick={() => setSelectedEvent(event)}
                getScopeColor={getScopeColor}
                getScopeIcon={getScopeIcon}
              />
            ))}
          </div>
        )}
      </main>
      
      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal 
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            loadEvents();
          }}
        />
      )}
      
      {/* Event Detail Modal */}
      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          onClose={() => setSelectedEvent(null)}
          onRSVP={handleRSVP}
          onUpdated={() => {
            setSelectedEvent(null);
            loadEvents();
          }}
          getScopeColor={getScopeColor}
        />
      )}
      
      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-sm mx-4 p-6" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
            <h3 className="font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
              Export Calendar (.ics)
            </h3>
            <p className="text-sm mb-4" style={{ color: 'var(--fy-text-secondary)' }}>
              Download your {scopeFilter === 'all' ? '' : scopeFilter} calendar for use in other apps.
            </p>
            <div className="space-y-2">
              <Button onClick={() => handleExport(30)} variant="outline" className="w-full justify-start">
                Next 30 days
              </Button>
              <Button onClick={() => handleExport(90)} variant="outline" className="w-full justify-start">
                Next 90 days
              </Button>
              <Button onClick={() => handleExport(0)} variant="outline" className="w-full justify-start">
                All future events
              </Button>
            </div>
            <Button onClick={() => setShowExportModal(false)} variant="ghost" className="w-full mt-4">
              Cancel
            </Button>
          </Card>
        </div>
      )}
    </div>
  );
}

// Event Card Component
function EventCard({ event, onRSVP, onClick, getScopeColor, getScopeIcon }) {
  const startTime = new Date(event.start_time);
  const endTime = new Date(event.end_time);
  
  return (
    <Card 
      className="p-4 cursor-pointer hover:shadow-md transition-shadow"
      style={{ backgroundColor: 'var(--fy-surface-card)', borderLeft: `4px solid ${getScopeColor(event.scope)}` }}
      onClick={onClick}
      data-testid={`event-${event.event_id}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span style={{ color: getScopeColor(event.scope) }}>
              {getScopeIcon(event.scope)}
            </span>
            <span className="text-xs font-medium capitalize" style={{ color: getScopeColor(event.scope) }}>
              {event.scope}
            </span>
            {event.series_id && (
              <span className="text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'var(--fy-surface-muted)', color: 'var(--fy-text-secondary)' }}>
                Recurring
              </span>
            )}
          </div>
          
          <h3 className="font-medium truncate" style={{ color: 'var(--fy-text-primary)' }}>
            {event.title}
          </h3>
          
          <div className="flex items-center gap-3 mt-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
            <span className="flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              {startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - {endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </span>
            {event.location && (
              <span className="flex items-center gap-1 truncate">
                <MapPin className="w-3.5 h-3.5" />
                {event.location}
              </span>
            )}
          </div>
        </div>
        
        {/* RSVP Status */}
        <div className="flex gap-1 ml-2" onClick={e => e.stopPropagation()}>
          {RSVP_OPTIONS.map(opt => (
            <button
              key={opt.status}
              onClick={() => onRSVP(event.event_id, opt.status)}
              className={`p-1.5 rounded-full transition-colors ${
                event.my_rsvp === opt.status ? 'ring-2' : ''
              }`}
              style={{
                backgroundColor: event.my_rsvp === opt.status ? opt.color + '20' : 'transparent',
                color: event.my_rsvp === opt.status ? opt.color : 'var(--fy-text-disabled)',
                ringColor: opt.color
              }}
              title={opt.label}
              data-testid={`rsvp-${opt.status}`}
            >
              <opt.icon className="w-4 h-4" />
            </button>
          ))}
        </div>
      </div>
      
      {/* RSVP Counts */}
      {event.rsvp_counts && Object.keys(event.rsvp_counts).length > 0 && (
        <div className="flex gap-3 mt-3 text-xs" style={{ color: 'var(--fy-text-secondary)' }}>
          {event.rsvp_counts.going && <span>{event.rsvp_counts.going} going</span>}
          {event.rsvp_counts.interested && <span>{event.rsvp_counts.interested} interested</span>}
        </div>
      )}
    </Card>
  );
}

// Create Event Modal
function CreateEventModal({ onClose, onCreated }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    location: '',
    start_date: '',
    start_time: '',
    end_date: '',
    end_time: '',
    scope: 'personal',
    group_id: '',
    commons_id: '',
    recurrence_rule: 'none',
    recurrence_end: ''
  });
  const [groups, setGroups] = useState([]);
  const [commons, setCommons] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  useEffect(() => {
    loadUserGroupsAndCommons();
  }, []);
  
  const loadUserGroupsAndCommons = async () => {
    try {
      const token = localStorage.getItem('token');
      const [groupsRes, commonsRes] = await Promise.all([
        axios.get(`${API}/api/groups/my-groups`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { groups: [] } })),
        axios.get(`${API}/api/commons/my-commons`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { commons: [] } }))
      ]);
      setGroups(groupsRes.data.groups || []);
      setCommons(commonsRes.data.commons || []);
    } catch (err) {
      console.error('Failed to load groups/commons:', err);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.start_date || !formData.start_time) {
      toast.error('Title, date, and start time are required');
      return;
    }
    
    if (formData.recurrence_rule !== 'none' && !formData.recurrence_end) {
      toast.error('End date required for recurring events');
      return;
    }
    
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');
      
      // Combine date and time
      const start_time = new Date(`${formData.start_date}T${formData.start_time}`).toISOString();
      const end_time = formData.end_date && formData.end_time 
        ? new Date(`${formData.end_date}T${formData.end_time}`).toISOString()
        : new Date(`${formData.start_date}T${formData.end_time || formData.start_time}`).toISOString();
      
      const payload = {
        title: formData.title,
        description: formData.description || null,
        location: formData.location || null,
        start_time,
        end_time,
        scope: formData.scope,
        group_id: formData.scope === 'group' ? formData.group_id : null,
        commons_id: formData.scope === 'commons' ? formData.commons_id : null,
        recurrence_rule: formData.recurrence_rule,
        recurrence_end: formData.recurrence_rule !== 'none' ? new Date(`${formData.recurrence_end}T23:59:59`).toISOString() : null
      };
      
      await axios.post(`${API}/api/calendar/events`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Event created');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create event');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-lg max-h-[90vh] overflow-y-auto" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
        <div className="sticky top-0 p-4 border-b flex items-center justify-between" style={{ backgroundColor: 'var(--fy-surface-card)', borderColor: 'var(--fy-border-subtle)' }}>
          <h3 className="font-semibold" style={{ color: 'var(--fy-text-primary)' }}>Create Event</h3>
          <Button onClick={onClose} variant="ghost" size="icon">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
              Title *
            </label>
            <input
              type="text"
              value={formData.title}
              onChange={e => setFormData({...formData, title: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border"
              style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
              placeholder="Event title"
              required
              data-testid="event-title"
            />
          </div>
          
          {/* Scope */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
              Event Type
            </label>
            <div className="flex gap-2">
              {['personal', 'group', 'commons'].map(scope => (
                <button
                  key={scope}
                  type="button"
                  onClick={() => setFormData({...formData, scope})}
                  className={`px-3 py-2 rounded-lg text-sm font-medium capitalize ${
                    formData.scope === scope ? 'ring-2' : ''
                  }`}
                  style={{
                    backgroundColor: formData.scope === scope ? 'var(--lk-primary-green)' : 'var(--fy-surface-muted)',
                    color: formData.scope === scope ? 'var(--lk-warm-sand)' : 'var(--fy-text-secondary)',
                    ringColor: 'var(--lk-primary-green)'
                  }}
                >
                  {scope}
                </button>
              ))}
            </div>
          </div>
          
          {/* Group/Commons selector */}
          {formData.scope === 'group' && groups.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Select Group
              </label>
              <select
                value={formData.group_id}
                onChange={e => setFormData({...formData, group_id: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
                required
              >
                <option value="">Select a group</option>
                {groups.map(g => (
                  <option key={g.group_id} value={g.group_id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {formData.scope === 'commons' && commons.length > 0 && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Select Commons
              </label>
              <select
                value={formData.commons_id}
                onChange={e => setFormData({...formData, commons_id: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
                required
              >
                <option value="">Select a commons</option>
                {commons.map(c => (
                  <option key={c.commons_id} value={c.commons_id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Start Date *
              </label>
              <input
                type="date"
                value={formData.start_date}
                onChange={e => setFormData({...formData, start_date: e.target.value, end_date: formData.end_date || e.target.value})}
                className="w-full px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
                required
                data-testid="event-start-date"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Start Time *
              </label>
              <input
                type="time"
                value={formData.start_time}
                onChange={e => setFormData({...formData, start_time: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
                required
                data-testid="event-start-time"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                End Date
              </label>
              <input
                type="date"
                value={formData.end_date}
                onChange={e => setFormData({...formData, end_date: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                End Time
              </label>
              <input
                type="time"
                value={formData.end_time}
                onChange={e => setFormData({...formData, end_time: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
              />
            </div>
          </div>
          
          {/* Location */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
              Location
            </label>
            <input
              type="text"
              value={formData.location}
              onChange={e => setFormData({...formData, location: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border"
              style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
              placeholder="Add location (optional)"
            />
          </div>
          
          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={e => setFormData({...formData, description: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border resize-none"
              style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
              rows={3}
              placeholder="Add description (optional)"
            />
          </div>
          
          {/* Recurrence */}
          <div>
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
              Repeat
            </label>
            <select
              value={formData.recurrence_rule}
              onChange={e => setFormData({...formData, recurrence_rule: e.target.value})}
              className="w-full px-3 py-2 rounded-lg border"
              style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
            >
              <option value="none">Does not repeat</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          
          {formData.recurrence_rule !== 'none' && (
            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--fy-text-primary)' }}>
                Repeat Until *
              </label>
              <input
                type="date"
                value={formData.recurrence_end}
                onChange={e => setFormData({...formData, recurrence_end: e.target.value})}
                className="w-full px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}
                required
              />
            </div>
          )}
          
          {/* Submit */}
          <div className="flex gap-2 pt-4">
            <Button type="button" onClick={onClose} variant="outline" className="flex-1">
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={submitting}
              className="flex-1"
              style={{ backgroundColor: 'var(--lk-primary-green)', color: 'var(--lk-warm-sand)' }}
              data-testid="create-event-submit"
            >
              {submitting ? 'Creating...' : 'Create Event'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

// Event Detail Modal
function EventDetailModal({ event, onClose, onRSVP, onUpdated, getScopeColor }) {
  const [showEditMode, setShowEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteMode, setDeleteMode] = useState('this_only');
  const [deleting, setDeleting] = useState(false);
  
  const startTime = new Date(event.start_time);
  const endTime = new Date(event.end_time);
  
  const handleDelete = async () => {
    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      const res = await axios.delete(`${API}/api/calendar/events/${event.event_id}?delete_mode=${deleteMode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.pending) {
        toast.info('Deletion pending second approval');
      } else {
        toast.success('Event deleted');
      }
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-lg" style={{ backgroundColor: 'var(--fy-surface-card)' }}>
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--fy-border-subtle)' }}>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: getScopeColor(event.scope) }} />
            <span className="text-sm font-medium capitalize" style={{ color: getScopeColor(event.scope) }}>
              {event.scope} Event
            </span>
          </div>
          <Button onClick={onClose} variant="ghost" size="icon">
            <X className="w-5 h-5" />
          </Button>
        </div>
        
        <div className="p-4">
          <h2 className="text-xl font-semibold mb-4" style={{ color: 'var(--fy-text-primary)' }}>
            {event.title}
          </h2>
          
          <div className="space-y-3 mb-6">
            <div className="flex items-center gap-3" style={{ color: 'var(--fy-text-secondary)' }}>
              <CalendarIcon className="w-5 h-5" />
              <span>{startTime.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</span>
            </div>
            <div className="flex items-center gap-3" style={{ color: 'var(--fy-text-secondary)' }}>
              <Clock className="w-5 h-5" />
              <span>
                {startTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })} - 
                {endTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </span>
            </div>
            {event.location && (
              <div className="flex items-center gap-3" style={{ color: 'var(--fy-text-secondary)' }}>
                <MapPin className="w-5 h-5" />
                <span>{event.location}</span>
              </div>
            )}
            {event.description && (
              <p className="text-sm mt-4" style={{ color: 'var(--fy-text-secondary)' }}>
                {event.description}
              </p>
            )}
          </div>
          
          {/* RSVP Section */}
          <div className="border-t pt-4 mb-4" style={{ borderColor: 'var(--fy-border-subtle)' }}>
            <p className="text-sm font-medium mb-3" style={{ color: 'var(--fy-text-primary)' }}>Your Response</p>
            <div className="flex gap-2">
              {RSVP_OPTIONS.map(opt => (
                <Button
                  key={opt.status}
                  onClick={() => onRSVP(event.event_id, opt.status)}
                  variant={event.my_rsvp === opt.status ? 'default' : 'outline'}
                  size="sm"
                  className="flex-1"
                  style={event.my_rsvp === opt.status ? { backgroundColor: opt.color, color: 'white' } : {}}
                >
                  <opt.icon className="w-4 h-4 mr-1" />
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>
          
          {/* Actions */}
          {event.scope === 'personal' && (
            <div className="flex gap-2">
              <Button onClick={() => setShowDeleteConfirm(true)} variant="outline" size="sm" className="text-red-500">
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </Button>
            </div>
          )}
        </div>
        
        {/* Delete Confirmation */}
        {showDeleteConfirm && (
          <div className="p-4 border-t" style={{ borderColor: 'var(--fy-border-subtle)', backgroundColor: 'var(--fy-surface-muted)' }}>
            <p className="text-sm font-medium mb-3" style={{ color: 'var(--fy-text-primary)' }}>
              Delete this event?
            </p>
            {event.series_id && (
              <div className="space-y-2 mb-3">
                <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  <input type="radio" name="deleteMode" value="this_only" checked={deleteMode === 'this_only'} onChange={e => setDeleteMode(e.target.value)} />
                  This event only
                </label>
                <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  <input type="radio" name="deleteMode" value="this_and_future" checked={deleteMode === 'this_and_future'} onChange={e => setDeleteMode(e.target.value)} />
                  This and future events
                </label>
                <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--fy-text-secondary)' }}>
                  <input type="radio" name="deleteMode" value="entire_series" checked={deleteMode === 'entire_series'} onChange={e => setDeleteMode(e.target.value)} />
                  Entire series
                </label>
              </div>
            )}
            <div className="flex gap-2">
              <Button onClick={() => setShowDeleteConfirm(false)} variant="outline" size="sm">Cancel</Button>
              <Button onClick={handleDelete} disabled={deleting} size="sm" className="bg-red-500 text-white hover:bg-red-600">
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
